import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * Запись голоса файлом на телефоне (решение владельца 02.10.2026, сверка `CHECK-2026-10-01-OFFLINE-AUDIO.md` §2).
 *
 * 🔴 ЗАЧЕМ. Очередь отправки лежит в IndexedDB, а её WebKit вправе стереть «при нехватке места и когда сайтом
 * давно не пользовались». Каталог приложения (`Directory.Library`) система не трогает до удаления самого
 * приложения — другого хранилища с такой гарантией на iPhone нет. Сюда ложатся только байты записи; сама
 * запись очереди (кому, какое задание, когда) остаётся в IndexedDB и держит на файл ссылку.
 *
 * ⚠️ ПЛАГИН ЗОВЁМ ПО ИМЕНИ, БЕЗ npm-ПАКЕТА В ИМПОРТАХ. Нативная часть `@capacitor/filesystem` (8.1.4) ставится
 * сборкой приложения; здесь — только мост `registerPlugin('Filesystem')` и узкий тип ровно тех трёх вызовов,
 * что нужны. Так модуль собирается и на сайте, и в приложении 1.1.0, где плагина нет: там `fsAvailable()`
 * отвечает «нет», и запись идёт прежним путём — байтами в очередь.
 *
 * ⚠️ ЧЕРЕЗ МОСТ ЕДЕТ base64: 4 МБ записи — это 5,6 МБ строки. Терпимо, но мерить на телефоне.
 */

type FsPlugin = {
  /** Без `encoding` плагин считает `data` строкой base64 и пишет двоичный файл. */
  writeFile(o: { path: string; data: string; directory: string; recursive?: boolean }): Promise<{ uri: string }>;
  /** Без `encoding` на телефоне отдаёт base64. */
  readFile(o: { path: string; directory: string }): Promise<{ data: string | Blob }>;
  deleteFile(o: { path: string; directory: string }): Promise<void>;
};

/**
 * `Directory.Library` плагина: `Library/` на iOS, каталог файлов приложения на Android.
 *
 * ⚠️ НЕ `Directory.Data`. По README плагина 8.1.4 «Data» на iOS — это каталог Documents (он же виден в
 * «Файлах», если приложение это разрешит), а не `Library/`, как записано в сверке 01.10.2026. Записи ученика
 * там делать нечего. `Cache` не годится тем более: его система чистит при нехватке места.
 */
const DIRECTORY = 'LIBRARY';
const FOLDER = 'records';

let plugin: FsPlugin | null = null;
const fs = (): FsPlugin => (plugin ??= registerPlugin<FsPlugin>('Filesystem'));

/** Ссылка на файл записи — лежит в записи очереди вместо самих байтов. */
export type RecordFileRef = { store: 'fs'; path: string; bytes: number };

export function isRecordFileRef(v: unknown): v is RecordFileRef {
  return !!v && typeof v === 'object' && (v as RecordFileRef).store === 'fs' && typeof (v as RecordFileRef).path === 'string';
}

/** Есть ли куда писать файл: приложение, и в нём стоит плагин. */
export function fsAvailable(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Filesystem');
  } catch {
    return false;
  }
}

/** Байты → base64 порциями: одним вызовом `String.fromCharCode` на мегабайты упирается в предел аргументов. */
export function bytesToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) {
    binary += String.fromCharCode(...bytes.subarray(i, i + step));
  }
  return btoa(binary);
}

export function base64ToBytes(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/** Имя файла — только то, что не сломает путь. */
const safeName = (name: string) => name.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 96) || 'record';

/**
 * Положить запись файлом. `null` — не вышло (нет плагина, нет места, мост отказал): место вызова кладёт
 * байты в очередь прежним путём. Молчаливой потери здесь быть не может — отказ виден по `null`.
 *
 * ⚠️ ПОСЛЕ ЗАПИСИ ФАЙЛ ПЕРЕЧИТЫВАЕТСЯ. «Плагин ответил без ошибки» — ещё не «байты на диске»: ссылка на
 * пустой или обрезанный файл превратила бы запись ученика в отказ сервера через неделю, когда связь появится.
 */
export async function saveRecordFile(name: string, audio: ArrayBuffer): Promise<RecordFileRef | null> {
  if (!fsAvailable() || !audio.byteLength) return null;
  const ref: RecordFileRef = { store: 'fs', path: `${FOLDER}/${safeName(name)}`, bytes: audio.byteLength };
  try {
    await fs().writeFile({ path: ref.path, data: bytesToBase64(audio), directory: DIRECTORY, recursive: true });
    const back = await readRecordFile(ref);
    if (back.byteLength !== audio.byteLength) throw new Error('size mismatch');
    return ref;
  } catch {
    await deleteRecordFile(ref);
    return null;
  }
}

/** Прочитать запись. Бросает, если файла нет или он пуст, — очередь должна узнать, что слать нечего. */
export async function readRecordFile(ref: RecordFileRef): Promise<ArrayBuffer> {
  const { data } = await fs().readFile({ path: ref.path, directory: DIRECTORY });
  const buf = typeof data === 'string' ? base64ToBytes(data) : await data.arrayBuffer();
  if (!buf.byteLength) throw new Error('record file empty');
  return buf;
}

/** Стереть файл записи. Отказ глотается: файла могло уже не быть. */
export async function deleteRecordFile(ref: RecordFileRef): Promise<void> {
  try {
    await fs().deleteFile({ path: ref.path, directory: DIRECTORY });
  } catch {
    /* нет файла — нечего стирать */
  }
}

/** Только для проверок: подменный плагин. */
export function __setFsPluginForTests(fake: FsPlugin | null): void {
  plugin = fake;
}
